import { Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { LabelWithTip } from "../components/InfoTip";
import MetricCard from "../components/MetricCard";
import ShareModal from "../components/ShareModal";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  getVisiblePages,
} from "../components/ui/pagination";
import styles from "./LeaderboardPage.module.css";
import { useLanguage } from "../LanguageContext";
import { useAuth } from "../AuthContext";
import { useLeaderboard } from "../usePlatformData";

// Drawdown limits until the API sends its rules
const DAILY_DD_LIMIT = 5;
const MAX_DD_LIMIT = 20;
const MEDALS = { 1: "🥇", 2: "🥈", 3: "🥉" };
const PODIUM_SIZE = 3;

const pageSize = 10;
const MOBILE_QUERY = "(max-width: 760px)";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => Boolean(window.matchMedia?.(MOBILE_QUERY).matches));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

const formatNumber = (n) => Math.round(n).toLocaleString("en-US");

// "—" until the figure loads, or while the platform doesn't track it yet
const formatFigure = (value, suffix = "") => (value == null ? "—" : `${formatNumber(value)}${suffix}`);

// One decimal place, without a trailing ".0"
const formatPct = (value) => String(Math.round(value * 10) / 10);

function initials(name) {
  return name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
}

// Yellow from half the limit (2.5% daily, 10% max), red once it's hit
function riskTone(value, limit) {
  if (value >= limit) return "danger";
  if (value >= limit / 2) return "warning";
  return "safe";
}

function ProgressMetric({ value, limit }) {
  const width = `${Math.min(100, (value / limit) * 100)}%`;
  const tone = riskTone(value, limit);
  return (
    <div className={`${styles.progressMetric} ${styles[`risk${tone[0].toUpperCase()}${tone.slice(1)}`]}`}>
      <div className={styles.progressTrack}>
        <span className={styles.progressFill} style={{ width }} />
      </div>
      <span className={styles.progressText}>
        {formatPct(value)}%
        <span className={styles.progressTextSuffix}>/{limit}%</span>
      </span>
    </div>
  );
}

function PositionCard({ rank, onShare }) {
  const { t } = useLanguage();
  return (
    <article className={styles.positionCard}>
      <p className={styles.positionLabel}>{t("lb_your_position")}</p>
      <div className={`${styles.positionRow} d-flex align-items-center justify-content-between`}>
        <strong className={styles.positionValue}>#{rank}</strong>
        <button type="button" className={styles.shareButton} onClick={onShare}>{t("lb_share")}</button>
      </div>
    </article>
  );
}

// Mobile only: top three as avatars, #1 raised in the middle
function Podium({ leaders, isYou }) {
  const order = [leaders[1], leaders[0], leaders[2]].filter(Boolean);
  return (
    <ol className={styles.podium}>
      {order.map((trader) => (
        <li
          key={trader.rank}
          className={`${styles.podiumItem} ${trader.rank === 1 ? styles.podiumFirst : ""}`}
        >
          <span className={`${styles.podiumAvatar} ${isYou(trader) ? styles.podiumAvatarYou : ""}`}>
            {initials(trader.name)}
            <span className={styles.podiumMedal} aria-label={`Rank ${trader.rank}`}>{MEDALS[trader.rank]}</span>
          </span>
          <span className={styles.podiumName}>{trader.name}</span>
          <span className={styles.podiumProfit}>{formatNumber(trader.prop)} PRP</span>
        </li>
      ))}
    </ol>
  );
}

// enrolled: false for free users who haven't started an account — no position card or "(you)" row
export default function LeaderboardPage({ enrolled = true }) {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [shareOpen, setShareOpen] = useState(false);
  const { user } = useAuth();
  const { data, status } = useLeaderboard();
  // The API rebuilds the board every few minutes, so show a just-saved name on your own row straight away
  const traders = useMemo(
    () =>
      (data?.traders ?? []).map((trader) =>
        trader.you && user.username ? { ...trader, name: user.username } : trader
      ),
    [data, user.username]
  );
  const dailyLimit = data?.rules.dailyLossPct ?? DAILY_DD_LIMIT;
  const maxLimit = data?.rules.maxDrawdownPct ?? MAX_DD_LIMIT;
  const you = enrolled ? traders.find((trader) => trader.you) : null;
  const isYou = (trader) => enrolled && trader.you;

  const overview = data?.overview;
  const overviewCards = [
    { label: t("lb_active_traders"), info: t("lb_tip_active"), value: formatFigure(overview?.activeTraders) },
    { label: t("lb_qualified_traders"), info: t("lb_tip_qualified"), value: formatFigure(overview?.qualifiedTraders) },
    { label: t("lb_total_prp"), info: t("lb_tip_total_prp"), value: formatFigure(overview?.totalPrp, " PRP") },
    {
      label: t("lb_total_usdc_paid"),
      info: t("lb_tip_usdc_paid"),
      value: overview?.totalUsdcPaid == null ? "—" : `$${formatNumber(overview.totalUsdcPaid)}`,
    },
  ];

  const searching = query.trim() !== "";
  // On mobile the top three move into the podium, so the list starts at #4
  const showPodium = isMobile && !searching;

  const filtered = useMemo(() => {
    const value = query.trim().toLowerCase();
    if (!value) return traders;
    return traders.filter((trader) => trader.name.toLowerCase().includes(value));
  }, [query, traders]);

  const listRows = showPodium ? filtered.slice(PODIUM_SIZE) : filtered;
  const totalPages = Math.max(1, Math.ceil(listRows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const visibleRows = listRows.slice((safePage - 1) * pageSize, safePage * pageSize);
  const pagination = getVisiblePages(totalPages, safePage);

  return (
    <section className={styles.page}>
      <section className={styles.hero}>
        <h1 className={styles.heroTitle}>{t("lb_title")}</h1>
        <div className={`${styles.liveRow} d-flex align-items-center`}>
          <span className={styles.liveBadge}>
            <span className={styles.liveDot} aria-hidden="true" />
            <span>{t("lb_live")}</span>
          </span>
          <p>{t("lb_live_update")}</p>
        </div>
      </section>

      {shareOpen && you && (
        <ShareModal
          rank={you.rank}
          propProfit={formatNumber(you.prop)}
          usdcPayouts={you.usdcPaid == null ? "—" : `$${formatNumber(you.usdcPaid)}`}
          onClose={() => setShareOpen(false)}
        />
      )}

      <section className={styles.summary}>
        <div className={`${styles.overviewGrid} ${you ? "" : styles.overviewGridFour}`}>
          {you && <PositionCard rank={you.rank} onShare={() => setShareOpen(true)} />}
          {overviewCards.map((card) => (
            <MetricCard key={card.label} label={card.label} info={card.info} value={card.value} />
          ))}
        </div>

        <div className={`${styles.tableTop} d-flex`}>
          <h2 className={styles.sectionTitle}>{t("lb_traders")}</h2>
          <label className={`${styles.searchWrap} d-flex align-items-center`}>
            <Search className={styles.searchIcon} aria-hidden="true" />
            <input
              type="search"
              placeholder={t("lb_search")}
              aria-label={t("lb_search")}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
            />
          </label>
        </div>
      </section>

      <section className={styles.tableSection}>
        {showPodium && safePage === 1 && <Podium leaders={filtered.slice(0, PODIUM_SIZE)} isYou={isYou} />}

        {visibleRows.length > 0 && (
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t("lb_rank")}</th>
                  <th>{t("lb_name")}</th>
                  <th>
                    <LabelWithTip label={t("lb_prop_profit")} text={t("lb_tip_prp_profit")} placement="bottom" />
                  </th>
                  <th className={styles.desktopCol}>{t("lb_status")}</th>
                  <th className={styles.desktopCol}>{t("lb_usdc_paid")}</th>
                  <th className={styles.desktopCol}>{t("lb_daily_dd")}</th>
                  <th className={styles.desktopCol}>{t("lb_max_dd")}</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((trader) => (
                  <tr key={trader.rank} className={isYou(trader) ? styles.highlightRow : ""}>
                    <td className={styles.rankCell}>
                      {MEDALS[trader.rank] ? <span className={styles.medal}>{MEDALS[trader.rank]}</span> : null}
                      <span>{trader.rank}</span>
                    </td>
                    <td className={styles.nameCell}>
                      <span>{trader.name}</span>
                      {isYou(trader) ? <span className={styles.youTag}> ({t("lb_you")})</span> : null}
                    </td>
                    <td className={styles.propCell}>{formatNumber(trader.prop)} PRP</td>
                    <td className={styles.desktopCol}>
                      <span className={`${styles.statusBadge} ${styles[`status${trader.status[0].toUpperCase()}${trader.status.slice(1)}`]}`}>
                        {t(`lb_status_${trader.status}`)}
                      </span>
                    </td>
                    <td className={`${styles.payoutCell} ${styles.desktopCol}`}>
                      {trader.usdcPaid == null ? "—" : `$${formatNumber(trader.usdcPaid)}`}
                    </td>
                    <td className={styles.desktopCol}>
                      <ProgressMetric value={trader.daily} limit={dailyLimit} />
                    </td>
                    <td className={styles.desktopCol}>
                      <ProgressMetric value={trader.max} limit={maxLimit} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {filtered.length === 0 && (
          <p className={styles.emptyState} role="status">
            {status === "loading"
              ? t("lb_loading")
              : status === "error"
              ? t("lb_load_error")
              : searching
              ? t("lb_no_results").replace("{query}", query.trim())
              : t("lb_empty")}
          </p>
        )}

        {totalPages > 1 ? (
          <Pagination>
            <PaginationContent className={styles.pagination}>
              {pagination.pages.map((pageNumber) => (
                <PaginationItem key={pageNumber}>
                  <PaginationLink
                    className={`${styles.pageButton} ${safePage === pageNumber ? styles.pageButtonActive : ""}`}
                    isActive={safePage === pageNumber}
                    onClick={() => setPage(pageNumber)}
                  >
                    {pageNumber}
                  </PaginationLink>
                </PaginationItem>
              ))}
              {pagination.showEllipsis ? (
                <PaginationItem>
                  <PaginationEllipsis
                    className={styles.pageDotsButton}
                    onClick={() => setPage(pagination.ellipsisTarget)}
                    aria-label={`Jump to page ${pagination.ellipsisTarget}`}
                  />
                </PaginationItem>
              ) : null}
            </PaginationContent>
          </Pagination>
        ) : null}
      </section>
    </section>
  );
}
